/**
 * stamp-options.js
 * Handles the stamp personalization form (snippets/stamp-options.liquid) on the
 * stamp-metafields product template.
 *
 * On add-to-cart it sends ONE /cart/add.js request containing:
 *   - the main product, carrying the text lines + ink colors + special
 *     instructions as line-item properties
 *   - each selected colour / add-on product as its own line item
 * then reuses the theme's existing cart drawer via the `cart:added` event.
 *
 * Loaded only on pages where the form actually renders (the snippet emits the
 * <script> tag itself), so it no-ops elsewhere.
 */
(function () {
  'use strict';

  if (window.__stampOptionsInit) return;
  window.__stampOptionsInit = true;

  /**
   * Available Pack Sizes REPLACES what is being bought rather than adding to it:
   * choosing a pack shows that pack's own price as the product price, and the
   * cart receives that pack as the single line item instead of this product
   * (see collectItems below).
   *
   * Only options carrying data-swap-price/data-swap-variant do this. An option
   * whose metafield is named in custom.non_pricing_metafields_name has neither,
   * so it changes nothing: the product keeps its own price and stays in the cart.
   */
  function formatMoney(cents) {
    if (window.themeFormatMoney) return window.themeFormatMoney(cents);
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function checkedSwap() {
    var els = document.querySelectorAll('[data-swap-variant]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) continue;
      if (el.disabled) continue;
      return el;
    }
    return null;
  }

  function syncOptionPrice() {
    var priceEl = document.getElementById('product-price');
    if (!priceEl) return;
    var base = parseInt(priceEl.getAttribute('data-base-price'), 10);
    if (isNaN(base)) return;

    var swap = checkedSwap();
    var cents = base;
    if (swap) {
      var swapCents = parseInt(swap.getAttribute('data-swap-price'), 10);
      if (swapCents > 0) cents = swapCents;
    }

    var formatted = formatMoney(cents);
    priceEl.textContent = formatted;

    var atcPriceEl = document.getElementById('atc-price');
    if (atcPriceEl) atcPriceEl.textContent = '— ' + formatted;
    var stickyPriceEl = document.getElementById('sticky-price');
    if (stickyPriceEl) stickyPriceEl.textContent = formatted;
  }

  // product-page.js calls this after a variant change resets the base price.
  window.stampSyncOptionPrice = syncOptionPrice;

  // Any option change inside the form re-syncs the price. This must not be
  // limited to options that carry data-swap-price: picking the free option
  // (which has none) has to clear a previously chosen pack's price too.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest('[data-stamp-options]')) syncOptionPrice();
  });

  // Reflect a preselected pack as soon as the page is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncOptionPrice);
  } else {
    syncOptionPrice();
  }

  /**
   * Mutually exclusive option groups (NEW vs OLD pad replacements): the two
   * lists are one decision, so choosing in one clears the selection in every
   * other group carrying the same data-exclusive key. Only the chosen group's
   * property is then submitted, because collectProperties skips unchecked
   * radios.
   */
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || t.type !== 'radio' || !t.closest) return;
    var group = t.closest('[data-exclusive]');
    if (!group) return;

    var key = group.getAttribute('data-exclusive');
    var scope = getStampForm(t) || document;
    var siblings = scope.querySelectorAll('[data-exclusive]');
    for (var i = 0; i < siblings.length; i++) {
      if (siblings[i] === group) continue;
      if (siblings[i].getAttribute('data-exclusive') !== key) continue;
      var radios = siblings[i].querySelectorAll('input[type="radio"]');
      for (var j = 0; j < radios.length; j++) radios[j].checked = false;
    }
  });

  function getStampForm(fromEl) {
    // The form is the product form; the options block lives inside it.
    var opts = document.querySelector('[data-stamp-options]');
    if (!opts) return null;
    return opts.closest('form[action*="/cart/add"]') || opts.closest('form');
  }

  function collectProperties(form) {
    var props = {};
    var els = form.querySelectorAll('[name^="properties["]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) continue;
      var m = el.name.match(/^properties\[(.+)\]$/);
      if (!m) continue;
      var val = (el.value || '').trim();
      if (!val) continue;
      props[m[1]] = val;
    }
    return props;
  }

  function collectItems(form) {
    var items = [];
    var idEl = form.querySelector('[name="id"]');
    var qtyEl = form.querySelector('[name="quantity"]');
    if (!idEl || !idEl.value) return null;

    var qty = parseInt(qtyEl && qtyEl.value, 10);
    if (!qty || qty < 1) qty = 1;

    // A chosen pack size replaces this product: the pack's own variant becomes
    // the single line item, so the customer is charged that pack's price once.
    var mainId = parseInt(idEl.value, 10);
    var swap = checkedSwap();
    if (swap) {
      var swapId = parseInt(swap.getAttribute('data-swap-variant'), 10);
      if (swapId) mainId = swapId;
    }

    var main = { id: mainId, quantity: qty };
    var props = collectProperties(form);
    if (Object.keys(props).length) main.properties = props;
    items.push(main);

    // Selected colour / add-on products -> each its own line item.
    // Handles both radio-card groups (:checked) and dropdowns (<select>).
    var addons = form.querySelectorAll('[data-addon]');
    for (var i = 0; i < addons.length; i++) {
      var el = addons[i];
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) continue;
      var v = parseInt(el.value, 10);
      if (v) items.push({ id: v, quantity: 1 });
    }

    return items;
  }

  function validate(form) {
    var missing = [];
    var groups = form.querySelectorAll('[data-required-group]');
    for (var i = 0; i < groups.length; i++) {
      // Required dropdown groups (State, Occupation, ...) hold a <select>;
      // required radio-card groups hold radios. Either way an empty value fails.
      var sel = groups[i].querySelector('select');
      if (sel) {
        if (!sel.value) missing.push(groups[i].getAttribute('data-label') || 'a required option');
        continue;
      }
      var checked = groups[i].querySelector('input[type="radio"]:checked');
      if (!checked || !checked.value) {
        missing.push(groups[i].getAttribute('data-label') || 'a required option');
      }
    }
    // Required text fields (e.g. Notary Name) — empty blocks add-to-cart
    var fields = form.querySelectorAll('[data-required-field]');
    for (var j = 0; j < fields.length; j++) {
      var inp = fields[j].querySelector('input, textarea');
      if (inp && !inp.value.trim()) {
        missing.push(fields[j].getAttribute('data-label') || 'a required field');
      }
    }
    return missing;
  }

  function showError(form, message) {
    var box = form.querySelector('[data-stamp-error]');
    if (!box) { window.alert(message); return; }
    box.textContent = message;
    box.hidden = false;
    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function clearError(form) {
    var box = form.querySelector('[data-stamp-error]');
    if (box) { box.hidden = true; box.textContent = ''; }
  }

  function setBusy(btn, busy) {
    if (!btn) return;
    btn.disabled = busy;
    btn.classList.toggle('is-loading', busy);
  }

  function addToCart(form, btn, goCheckout) {
    clearError(form);

    var missing = validate(form);
    if (missing.length) {
      showError(form, 'Please complete: ' + missing.join(', ') + '.');
      return;
    }

    var items = collectItems(form);
    if (!items) return;

    setBusy(btn, true);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (e) { throw e; }, function () { throw new Error('add failed'); });
        }
        // Buy It Now: everything is in the cart with its properties/add-ons —
        // go straight to checkout (button stays busy until navigation).
        if (goCheckout) {
          window.location.href = '/checkout';
          return null;
        }
        // Item(s) added — refresh the full cart for the drawer (a failed
        // refresh must NOT be reported as a failed add).
        return fetch('/cart.js')
          .then(function (cr) { return cr.ok ? cr.json() : null; })
          .catch(function () { return null; });
      })
      .then(function (cart) {
        if (goCheckout) return;
        setBusy(btn, false);
        if (btn) {
          btn.classList.add('is-added');
          setTimeout(function () { btn.classList.remove('is-added'); }, 1800);
        }
        if (cart) {
          document.dispatchEvent(new CustomEvent('cart:added', { detail: { cart: cart } }));
        }
      })
      .catch(function (err) {
        setBusy(btn, false);
        showError(form, (err && err.description) || 'Sorry, something went wrong adding this to your cart. Please try again.');
      });
  }

  // Native form submit (main ATC is type=submit inside the form, and Enter key)
  document.addEventListener('submit', function (e) {
    var form = e.target.closest ? e.target.closest('[data-stamp-form]') : null;
    if (!form) return;
    e.preventDefault();
    var btn = (e.submitter) || form.querySelector('[data-stamp-add]') || form.querySelector('[type="submit"]');
    addToCart(form, btn);
  });

  // Buttons outside the form (e.g. the sticky add-to-cart bar)
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-stamp-add]') : null;
    if (!btn) return;
    // If it's the submit button inside the form, the submit handler owns it.
    if (btn.type === 'submit' && btn.closest('[data-stamp-form]')) return;
    e.preventDefault();
    var form = getStampForm(btn);
    if (form) addToCart(form, btn);
  });

  // Buy It Now — same validated payload as ATC, then straight to checkout
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-stamp-buy-now]') : null;
    if (!btn) return;
    e.preventDefault();
    var form = getStampForm(btn);
    if (form) addToCart(form, btn, true);
  });
})();
